import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import PlanSelectionOnboarding from "./components/PlanSelectionOnboarding";
import PreBillingLaunchGuide from "./components/PreBillingLaunchGuide";

// Handle Shopify OAuth flow for both direct access and embedded app installs
const params = new URLSearchParams(window.location.search);
const shop = params.get("shop");
const host = params.get("host");
const hmac = params.get("hmac");
const code = params.get("code");  // OAuth authorization code from Shopify
const state = params.get("state");
const timestamp = params.get("timestamp");
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY || '';

/**
 * Wait for Shopify App Bridge to be ready and get session token
 * For managed installations, this is the ONLY way to get a valid token
 */
async function getSessionTokenFromAppBridge(maxRetries = 10, retryDelay = 500) {
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Check if shopify global is available (App Bridge CDN script loaded)
    if (window.shopify && typeof window.shopify.idToken === 'function') {
      try {
        const token = await window.shopify.idToken();
        if (token) {
          return token;
        }
      } catch (e) {
        console.warn('⚠️ App Bridge idToken() failed (attempt ' + (attempt + 1) + '):', e.message);
      }
    }
    
    // Wait before next attempt
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }
  
  console.warn('❌ Could not get session token from App Bridge after ' + maxRetries + ' attempts');
  return null;
}

/**
 * Perform token exchange with the backend
 * This is used for App Store managed installations where OAuth callback is not triggered
 */
async function performTokenExchange(sessionToken) {
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/shopify/token-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        session_token: sessionToken
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      const errorText = await response.text();
      console.error('❌ Token exchange failed:', response.status, errorText);
      return { success: false, error: errorText, status: response.status };
    }
  } catch (error) {
    console.error('❌ Token exchange error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if a shop exists in the backend database.
 * A missing shipping origin is valid during first-install Setup and must not
 * cause repeat token-exchange registration attempts.
 */
async function checkShopExists(shopDomain) {
  try {
    const checkUrl = `${BACKEND_URL}/api/shipping-origin/${encodeURIComponent(shopDomain)}`;
    const response = await fetch(checkUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error checking if shop exists:', error);
    return false;
  }
}

/**
 * Check whether this shop still needs to select a plan.
 * Returns true if plan_selected === false in the database.
 */
async function checkNeedsPlanSelection(shopDomain) {
  try {
    const res = await fetch(
      `${BACKEND_URL}/api/setup/status?shop=${encodeURIComponent(shopDomain)}`,
      { headers: { 'x-api-key': API_KEY } }
    );
    if (res.ok) {
      const data = await res.json();
      console.log('[TDK] plan_selected check result:', data);
      return data.planSelected === false;
    }
  } catch (e) {
    console.warn('[TDK] Could not check plan_selected, defaulting to App:', e.message);
  }
  return false;
}

// Function to check if shop exists in backend and trigger OAuth if needed
async function checkShopAndAuth() {
  
  // Case 0: OAuth callback from Shopify (traditional OAuth - direct link installs)
  // This still works for non-App Store installations
  if (code && shop) {
    
    // Build the backend callback URL with all OAuth params
    let callbackUrl = `${BACKEND_URL}/api/shopify/callback?`;
    callbackUrl += `code=${encodeURIComponent(code)}`;
    callbackUrl += `&shop=${encodeURIComponent(shop)}`;
    if (hmac) callbackUrl += `&hmac=${encodeURIComponent(hmac)}`;
    if (state) callbackUrl += `&state=${encodeURIComponent(state)}`;
    if (timestamp) callbackUrl += `&timestamp=${encodeURIComponent(timestamp)}`;
    if (host) callbackUrl += `&host=${encodeURIComponent(host)}`;
    
    // Redirect to backend to complete OAuth flow
    // For embedded apps in iframe, we need to break out
    if (window.top !== window.self) {
      // In iframe - need to redirect at top level
      window.top.location.href = callbackUrl;
    } else {
      // Not in iframe - direct redirect
      window.location.href = callbackUrl;
    }
    return;
  }
  
  if (!shop) {
    // No shop param - just render the app
    await renderApp();
    return;
  }

  // Case 1: Direct access (not in iframe) - always redirect to OAuth
  if (window.top === window.self) {
    let redirectUrl = `${BACKEND_URL}/api/auth?shop=${encodeURIComponent(shop)}`;
    if (host) redirectUrl += `&host=${encodeURIComponent(host)}`;
    if (hmac) redirectUrl += `&hmac=${encodeURIComponent(hmac)}`;
    window.location.assign(redirectUrl);
    return;
  }

  // Case 2: Embedded in Shopify Admin (iframe) - This is App Store managed installation
  
  // First check if shop already exists in database
  const shopExists = await checkShopExists(shop);
  
  if (shopExists) {
    await renderApp();
    return;
  }
  
  // Shop doesn't exist - this is likely a fresh App Store installation
  // We need to use Token Exchange to register the shop
  
  // Get session token from App Bridge (this is the key for managed installations)
  const sessionToken = await getSessionTokenFromAppBridge();
  
  if (sessionToken) {
    const result = await performTokenExchange(sessionToken);
    
    if (result.success) {
      await renderApp();
      return;
    } else {
      console.error('❌ Token exchange failed:', result.error);
      
      // If token exchange fails, still render the app
      // The app might show an error state, but at least it loads
      await renderApp();
      return;
    }
  } else {
    console.warn('⚠️ No session token available from App Bridge');
    
    // Fallback: Try traditional OAuth redirect
    // This might work for some edge cases
    const installUrl = `${BACKEND_URL}/api/shopify/install?shop=${encodeURIComponent(shop)}&embedded=1`;
    
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = installUrl;
      } else {
        window.location.href = installUrl;
      }
    } catch (e) {
      console.warn('Redirect blocked:', e);
      // Just render the app anyway
      await renderApp();
    }
  }
}

/**
 * renderApp — checks plan_selected BEFORE mounting any React component.
 *
 * Strategy: show a loading spinner immediately, then hit /api/setup/status.
 * If planSelected===false  → mount PlanSelectionOnboarding (App never mounts).
 * If planSelected===true   → mount App as normal.
 *
 * Because we decide BEFORE mounting App, there is zero possibility of a
 * React state race condition — App's loadData() setState cascade never runs.
 */
async function renderApp() {
  const root = ReactDOM.createRoot(document.getElementById("root"));

  // Show a branded loading spinner immediately — nothing else renders
  root.render(
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',
      flexDirection:'column',gap:'1rem',background:'#0f172a',color:'#fff'}}>
      <div style={{width:40,height:40,border:'3px solid #6366f1',borderTopColor:'transparent',
        borderRadius:'50%',animation:'tdkSpin 0.8s linear infinite'}} />
      <p style={{fontSize:'0.9rem',opacity:0.7,fontFamily:'system-ui,sans-serif',margin:0}}>
        Loading Tax &amp; Duty King…
      </p>
      <style>{`@keyframes tdkSpin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // Trial-first onboarding: merchants may configure and explore the app
  // before choosing a paid plan. Backend entitlements still protect calculations
  // after expiry, while TrialStatusNotice provides a recovery path.
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

/**
 * Keeps the guide and plan choice within the existing embedded-app bootstrap.
 * The guide calls no store APIs and does not expose app functionality before billing.
 */
function PreBillingGate({ shopDomain }) {
  const [showPlans, setShowPlans] = React.useState(false);
  return showPlans
    ? <PlanSelectionOnboarding shopDomain={shopDomain} />
    : <PreBillingLaunchGuide onChoosePlan={() => setShowPlans(true)} />;
}

// Start the auth check
checkShopAndAuth();
